/**
 * IndexedDB service for storing pending uploads when offline
 */

const DB_NAME = 'anhonestobserver_offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_uploads';

export interface PendingUpload {
  id: string;
  content: string;
  delimiter: string;
  source: string;
  autoCategorize: boolean;
  userId: string;
  timestamp: number;
  retryCount: number;
  error?: string;
}

class OfflineStorageService {
  private db: IDBDatabase | null = null;

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('userId', 'userId', { unique: false });
        }
      };
    });
  }

  /**
   * Add a pending upload to the store
   */
  async addPendingUpload(upload: Omit<PendingUpload, 'id' | 'timestamp' | 'retryCount'>): Promise<string> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const pendingUpload: PendingUpload = {
        ...upload,
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        retryCount: 0,
      };

      const request = store.add(pendingUpload);

      request.onsuccess = () => {
        resolve(pendingUpload.id);
      };

      request.onerror = () => {
        reject(new Error('Failed to add pending upload'));
      };
    });
  }

  /**
   * Get all pending uploads
   */
  async getAllPendingUploads(): Promise<PendingUpload[]> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(new Error('Failed to get pending uploads'));
      };
    });
  }

  /**
   * Get pending uploads for a specific user
   */
  async getPendingUploadsByUser(userId: string): Promise<PendingUpload[]> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(new Error('Failed to get pending uploads for user'));
      };
    });
  }

  /**
   * Delete a pending upload
   */
  async deletePendingUpload(id: string): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to delete pending upload'));
      };
    });
  }

  /**
   * Update a pending upload (e.g., increment retry count or update error)
   */
  async updatePendingUpload(id: string, updates: Partial<PendingUpload>): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const upload = getRequest.result;
        if (!upload) {
          reject(new Error('Upload not found'));
          return;
        }

        const updatedUpload = { ...upload, ...updates };
        const putRequest = store.put(updatedUpload);

        putRequest.onsuccess = () => {
          resolve();
        };

        putRequest.onerror = () => {
          reject(new Error('Failed to update pending upload'));
        };
      };

      getRequest.onerror = () => {
        reject(new Error('Failed to get pending upload'));
      };
    });
  }

  /**
   * Clear all pending uploads
   */
  async clearAllPendingUploads(): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to clear pending uploads'));
      };
    });
  }

  /**
   * Reset the database connection (test-only method)
   * @internal This method is intended for testing purposes only
   */
  resetForTesting(): void {
    this.db = null;
  }

  /**
   * Get count of pending uploads
   */
  async getPendingUploadCount(userId?: string): Promise<number> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      if (userId) {
        const index = store.index('userId');
        const request = index.count(userId);

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(new Error('Failed to count pending uploads'));
        };
      } else {
        const request = store.count();

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(new Error('Failed to count pending uploads'));
        };
      }
    });
  }
}

// Export singleton instance
const offlineStorage = new OfflineStorageService();
export default offlineStorage;
